import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaymentMethod } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import Marca from '../componentes/Marca';
import { converterOrcamento, enfileirarImpressao, obterOrcamento } from '../lib/consultas';
import { montarDocumentoOrcamento } from '../lib/orcamentoDoc';
import { baixarOrcamento, orcamentoParaPdf } from '../lib/orcamentoPdf';
import { brl, dataHoraBR, hojeISO, qtd } from '../lib/formato';
import { codigoDaPeca } from '../lib/codigoDaPeca';
import {
  dataCurtaDeISO, linkWhatsApp, situacaoOrcamento, textoOrcamento, type SituacaoOrcamento,
} from '../lib/orcamento';
import { mensagemDeErro } from '../lib/supabase';
import { FORMAS, type CarrinhoPrefill } from './Venda';

// Tela 15 — o documento do orçamento. A prévia na tela É o papel: fundo
// branco, tinta preta, e o imprimir esconde todo o resto do app (print:hidden
// no Layout e nas ações). Daqui saem os três destinos do orçamento:
//   * papel (imprimir) e WhatsApp (texto pronto) — para o cliente levar;
//   * virar venda — UMA RPC converte no preço prometido, na validade;
//   * emitir igual — venceu? o carrinho volta pronto para a tela de Venda.

const BADGE: Record<SituacaoOrcamento, { rotulo: string; classe: string }> = {
  aberto:     { rotulo: 'Aberto',     classe: 'border-ok/60 text-ok' },
  convertido: { rotulo: 'Virou venda', classe: 'border-edge text-dim' },
  vencido:    { rotulo: 'Vencido',    classe: 'border-alerta/60 text-alerta' },
};

export default function Orcamento() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cliente = useQueryClient();
  const [convertendo, setConvertendo] = useState(false);
  const [forma, setForma] = useState<PaymentMethod>('dinheiro');
  const [vendaGerada, setVendaGerada] = useState<string | null>(null);

  const consulta = useQuery({
    queryKey: ['orcamento', id],
    queryFn: () => obterOrcamento(id!),
    enabled: Boolean(id),
  });

  // Impressora da loja, pela fila (022/045): é o caminho dos computadores e
  // celulares que NÃO têm a impressora instalada — o agente no computador
  // principal imprime. O "Imprimir" comum continua ao lado, para quem tem.
  const paraLoja = useMutation({
    mutationFn: () => enfileirarImpressao({
      tipo: 'orcamento',
      titulo: `Orçamento ${consulta.data!.codigo}${
        consulta.data!.cliente_nome ? ` — ${consulta.data!.cliente_nome}` : ''}`,
      documentoHtml: montarDocumentoOrcamento(consulta.data!),
      refTable: 'quotes',
      refId: consulta.data!.id,
    }),
  });

  const converter = useMutation({
    mutationFn: () => converterOrcamento({ quoteId: id!, forma }),
    onSuccess: (saleId) => {
      setVendaGerada(saleId);
      setConvertendo(false);
      cliente.invalidateQueries({ queryKey: ['orcamento', id] });
      cliente.invalidateQueries({ queryKey: ['orcamentos'] });
      cliente.invalidateQueries({ queryKey: ['vendas'] });
      cliente.invalidateQueries({ queryKey: ['caixa'] });
      cliente.invalidateQueries({ queryKey: ['buscar'] });
      cliente.invalidateQueries({ queryKey: ['reposicao'] });
    },
  });

  if (consulta.isLoading) {
    return <Layout titulo="Orçamento"><Carregando texto="Carregando orçamento" /></Layout>;
  }
  const o = consulta.data;
  if (!o) {
    return (
      <Layout titulo="Orçamento">
        <Aviso tipo="erro">
          {consulta.error ? mensagemDeErro(consulta.error) : 'Orçamento não encontrado.'}
        </Aviso>
      </Layout>
    );
  }

  const situacao = situacaoOrcamento(o, hojeISO());
  const badge = BADGE[situacao];
  const desconto = Number(o.desconto);
  const total = Number(o.total);
  const somaPecas = total + desconto;
  const pecaForaDoCatalogo = o.itens.some((i) => !i.product_id);

  const textoZap = textoOrcamento(
    {
      codigo: o.codigo,
      cliente_nome: o.cliente_nome,
      desconto,
      total,
      valido_ate: o.valido_ate,
      vendedor: o.vendedor,
    },
    o.itens.map((i) => ({
      descricao: i.descricao,
      quantidade: Number(i.quantidade),
      preco_unit: Number(i.preco_unit),
      subtotal: Number(i.subtotal),
    })),
  );

  // "Emitir igual" vai para a aba ORÇAR (19/08/2026): quem repete um orçamento
  // quer outro orçamento, não uma venda — antes caía em /venda e o vendedor
  // tinha de achar o caminho de volta.
  //
  // E leva a DESCRIÇÃO congelada de cada item: sem ela, a PEÇA DIVERSA voltava
  // como "PEÇA DIVERSA — BALCÃO" e o nome que o cliente leu no papel ("Retrovisor
  // Uno 98 LD") se perdia — logo no caso em que ele volta justamente para
  // confirmar aquela peça.
  const emitirIgual = () => navigate('/orcar', {
    state: {
      carrinho: {
        itens: o.itens
          .filter((i) => i.product_id)
          .map((i) => ({
            product_id: i.product_id!,
            quantidade: Number(i.quantidade),
            preco_unit: Number(i.preco_unit),
            descricao: i.descricao,
            // 071: o código volta junto — quem refaz o orçamento não deveria
            // ter de procurar no catálogo do fornecedor de novo.
            codigo: i.codigo_avulso ?? undefined,
          })),
        desconto: desconto || undefined,
        cliente: o.cliente_nome,
        contato: o.cliente_contato,
        veiculo: o.veiculo,
      } satisfies CarrinhoPrefill,
    },
  });

  return (
    <Layout titulo={`Orçamento ${o.codigo}`}>
      <div className="space-y-4">
        <div className="flex items-center justify-between print:hidden">
          <span className={`border px-2 py-1 font-mono text-xs uppercase ${badge.classe}`}>
            {badge.rotulo}
          </span>
          {situacao === 'vencido' && (
            <p className="text-xs text-alerta">Venceu em {dataCurtaDeISO(o.valido_ate)}.</p>
          )}
          {situacao === 'convertido' && o.convertido_em && (
            <p className="text-xs text-dim">Virou venda em {dataHoraBR(o.convertido_em)}.</p>
          )}
        </div>

        {vendaGerada && (
          <Aviso tipo="ok">
            Venda registrada no preço prometido — estoque baixado e caixa lançado.
            <span className="ml-1 font-mono text-xs opacity-70">#{vendaGerada.slice(0, 8)}</span>
          </Aviso>
        )}

        {/* O DOCUMENTO — prévia na tela idêntica ao papel. Cores fixas (branco/
            preto) de propósito: isto imprime, o resto do app não. */}
        <section className="border border-edge bg-white p-5 text-neutral-900 sm:p-8 print:border-0 print:p-0">
          <header className="flex items-start justify-between gap-4 border-b-2 border-neutral-900 pb-4">
            <div className="flex items-center gap-3">
              <Marca className="h-9 w-auto text-marca" />
              <div>
                <p className="text-lg font-black leading-tight tracking-tight">SERGINHO AUTOPEÇAS</p>
                <p className="text-xs text-neutral-500">Rio Grande — RS</p>
              </div>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-mono text-sm font-bold">{o.codigo}</p>
              <p className="text-xs text-neutral-500">emitido {dataHoraBR(o.created_at)}</p>
              <p className="text-xs font-bold">válido até {dataCurtaDeISO(o.valido_ate)}</p>
            </div>
          </header>

          <div className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-neutral-300 py-3 text-sm">
            <p><span className="text-neutral-500">Cliente:</span> {o.cliente_nome ?? '—'}
              {o.cliente_contato && <span className="text-neutral-500"> · {o.cliente_contato}</span>}
            </p>
            {o.veiculo && (
              <p><span className="text-neutral-500">Veículo:</span> {o.veiculo}</p>
            )}
            <p><span className="text-neutral-500">Atendimento:</span> {o.vendedor ?? '—'}</p>
          </div>

          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left font-mono text-[0.625rem] uppercase tracking-wider text-neutral-500">
                <th className="py-1.5 pr-2 font-normal">Qtd.</th>
                <th className="py-1.5 pr-2 font-normal">Peça</th>
                {/* A COLUNA DA LOJA (10/09/2026, pedido do dono). `print:hidden`
                    não é detalhe: esta prévia é a folha que sai no Ctrl+P, e o
                    código é nosso — serve para pedir a peça ao fornecedor e
                    achar o similar, não para o cliente ler. O PDF e o documento
                    da fila de impressão são outros caminhos, e os dois já têm
                    teste provando que o código não aparece neles. */}
                <th className="py-1.5 pr-2 font-normal print:hidden">Código</th>
                <th className="py-1.5 pr-2 text-right font-normal">Preço un.</th>
                <th className="py-1.5 text-right font-normal">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {o.itens.map((i) => {
                const precoTabela = i.preco_tabela === null ? null : Number(i.preco_tabela);
                const temDePor = precoTabela !== null && precoTabela > Number(i.preco_unit);
                return (
                  <tr key={i.id} className="border-b border-neutral-200 align-top">
                    <td className="py-2 pr-2 tabular-nums">{qtd(Number(i.quantidade))}</td>
                    <td className="py-2 pr-2">{i.descricao}</td>
                    {/* Só na tela — ver o cabeçalho desta coluna. */}
                    <td className="py-2 pr-2 font-mono text-xs text-neutral-500 print:hidden">
                      {codigoDaPeca(i) || '—'}
                    </td>
                    <td className="py-2 pr-2 text-right tabular-nums">
                      {temDePor && (
                        <span className="mr-1 text-xs text-neutral-400 line-through">{brl(precoTabela)}</span>
                      )}
                      {brl(Number(i.preco_unit))}
                    </td>
                    <td className="py-2 text-right font-semibold tabular-nums">{brl(Number(i.subtotal))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="ml-auto mt-3 max-w-64 space-y-1 text-sm">
            {desconto > 0 && (
              <>
                <div className="flex justify-between"><span className="text-neutral-500">Peças</span>
                  <span className="tabular-nums">{brl(somaPecas)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Desconto</span>
                  <span className="tabular-nums">−{brl(desconto)}</span></div>
              </>
            )}
            <div className="flex items-baseline justify-between border-t-2 border-neutral-900 pt-2">
              <span className="font-mono text-xs uppercase tracking-wider">Total</span>
              <span className="text-2xl font-black tabular-nums">{brl(total)}</span>
            </div>
          </div>

          {o.observacao && <p className="mt-4 text-xs text-neutral-600">Obs.: {o.observacao}</p>}
          <p className="mt-4 border-t border-neutral-300 pt-3 text-[0.6875rem] leading-relaxed text-neutral-500">
            Preços válidos até {dataCurtaDeISO(o.valido_ate)}. Peças sujeitas a disponibilidade de
            estoque. Este documento não é reserva de mercadoria nem comprovante de pagamento.
          </p>
        </section>

        {/* Ações — nada disto sai no papel. */}
        <section className="space-y-3 print:hidden">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" className="botao-primario py-3"
                    disabled={paraLoja.isPending || paraLoja.isSuccess || !consulta.data}
                    onClick={() => paraLoja.mutate()}>
              {paraLoja.isSuccess ? 'Na fila da loja ✓'
                : paraLoja.isPending ? 'Enviando…' : 'Imprimir na loja'}
            </button>
            <button type="button" className="botao-secundario py-3" onClick={() => window.print()}>
              Imprimir aqui
            </button>
            {/* O PDF com a MARCA, o CNPJ e o endereço — o documento que o
                cliente leva embora ou recebe pelo WhatsApp. Diferente do
                "Imprimir", que usa a folha de estilo da própria tela. */}
            <button type="button" className="botao-secundario py-3"
                    onClick={() => consulta.data && baixarOrcamento(orcamentoParaPdf(consulta.data))}>
              Salvar PDF
            </button>
            <a
              className="botao-secundario py-3"
              href={linkWhatsApp(textoZap, o.cliente_contato)}
              target="_blank"
              rel="noreferrer"
            >
              WhatsApp
            </a>
            {paraLoja.error ? (
              <p className="col-span-2 text-xs text-erro">{mensagemDeErro(paraLoja.error)}</p>
            ) : null}
          </div>

          {situacao === 'aberto' && !convertendo && (
            <button type="button" className="botao-primario w-full py-4 text-lg"
                    onClick={() => setConvertendo(true)}>
              Virar venda · {brl(total)}
            </button>
          )}

          {convertendo && (
            <div className="cartao space-y-3 p-4">
              <p className="text-xs text-mute">
                A venda sai no preço prometido do orçamento, numa transação só: baixa o estoque e
                lança no caixa agora. Se faltar peça, nada acontece.
              </p>
              <div>
                <p className="rotulo">Forma de pagamento</p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {FORMAS.map((f) => (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setForma(f.id)}
                      aria-pressed={forma === f.id}
                      className={[
                        'border px-3 py-3 text-sm font-bold transition-colors',
                        forma === f.id
                          ? 'border-mute bg-edge text-paper'
                          : 'border-edge text-mute hover:border-signal hover:text-paper',
                      ].join(' ')}
                    >
                      {f.rotulo}
                    </button>
                  ))}
                </div>
              </div>
              {converter.error ? <Aviso tipo="erro">{mensagemDeErro(converter.error)}</Aviso> : null}
              <div className="flex gap-2">
                <button type="button" className="botao-secundario flex-1 py-3"
                        onClick={() => setConvertendo(false)}>
                  Voltar
                </button>
                <button type="button" className="botao-primario flex-1 py-3"
                        disabled={converter.isPending}
                        onClick={() => converter.mutate()}>
                  {converter.isPending ? 'Registrando…' : `Confirmar venda · ${brl(total)}`}
                </button>
              </div>
            </div>
          )}

          {situacao !== 'convertido' && (
            <button
              type="button"
              className="botao-secundario w-full py-3"
              onClick={emitirIgual}
              disabled={pecaForaDoCatalogo}
            >
              {situacao === 'vencido' ? 'Emitir igual com validade nova' : 'Emitir igual (novo orçamento)'}
            </button>
          )}
          {pecaForaDoCatalogo && (
            <p className="text-xs text-alerta">
              Há item cuja peça saiu do catálogo — este orçamento não pode ser reemitido nem virar
              venda; monte um novo pela tela de Venda.
            </p>
          )}
        </section>
      </div>
    </Layout>
  );
}
